import { useEffect, useMemo, useState } from 'react';
import { useCanonical } from '@/hooks/useCanonical';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, User, ChevronRight, CheckCircle2, List, X, Printer } from 'lucide-react';
import { getBlogArticleBySlug, getRelatedArticles, getArticleTags, getArticleAuthor } from '@/data/blog-articles';
import { injectInternalLinks } from '@/lib/internal-links';
import CTASection from '@/components/CTASection';
import BlogCard from '@/components/blog/BlogCard';
import AuthorCard from '@/components/blog/AuthorCard';
import ShareButtons from '@/components/blog/ShareButtons';
import ArticleTags from '@/components/blog/ArticleTags';

const extractHeadings = (html: string) => {
  const regex = /<h([23])[^>]*>(.*?)<\/h\1>/gi;
  const headings: { id: string; text: string; level: number }[] = [];
  let match;
  while ((match = regex.exec(html)) !== null) {
    const level = parseInt(match[1]);
    const text = match[2].replace(/<[^>]*>/g, '');
    const id = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    headings.push({ id, text, level });
  }
  return headings;
};

const processContent = (html: string) => {
  return html.replace(/<h([23])([^>]*)>(.*?)<\/h\1>/gi, (_, level, attrs, text) => {
    const cleanText = text.replace(/<[^>]*>/g, '');
    const id = cleanText.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return `<h${level}${attrs} id="${id}">${text}</h${level}>`;
  });
};

const generateTocNumbers = (headings: { level: number }[]) => {
  let h2Count = 0;
  let h3Count = 0;
  return headings.map((h) => {
    if (h.level === 2) { h2Count++; h3Count = 0; return `${h2Count}`; }
    else { h3Count++; return `${h2Count}.${h3Count}`; }
  });
};

const BlogArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  useCanonical(`/blog/${slug || ''}`);
  const article = slug ? getBlogArticleBySlug(slug) : undefined;
  const related = article ? getRelatedArticles(article) : [];
  const [activeId, setActiveId] = useState<string>('');
  const [tocOpen, setTocOpen] = useState(false);

  const headings = useMemo(() => (article ? extractHeadings(article.content) : []), [article]);
  const tocNumbers = useMemo(() => generateTocNumbers(headings), [headings]);
  const processedContent = useMemo(() => {
    if (!article) return '';
    const withIds = processContent(article.content);
    return injectInternalLinks(withIds, article.slug);
  }, [article]);
  const tags = article ? getArticleTags(article) : [];
  const author = article ? getArticleAuthor(article) : '';
  const articleUrl = typeof window !== 'undefined' ? window.location.href : '';

  useEffect(() => {
    if (article) {
      document.title = article.metaTitle;
      document.querySelector('meta[name="description"]')?.setAttribute('content', article.metaDescription);

      // JSON-LD BlogPosting schema
      const existingLd = document.getElementById('article-jsonld');
      if (existingLd) existingLd.remove();

      const script = document.createElement('script');
      script.id = 'article-jsonld';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: article.title,
        description: article.metaDescription,
        datePublished: article.date,
        dateModified: article.date,
        author: {
          '@type': 'Person',
          name: author,
          jobTitle: 'Infirmière diplômée & Fondatrice',
          worksFor: {
            '@type': 'Organization',
            name: 'Body by CC',
            url: 'https://bodybycc.fr',
          },
        },
        publisher: {
          '@type': 'Organization',
          name: 'Body by CC',
          url: 'https://bodybycc.fr',
          logo: {
            '@type': 'ImageObject',
            url: 'https://bodybycc.fr/og-image.png',
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://bodybycc.fr/blog/${slug}`,
        },
        keywords: article.keywords.join(', '),
        articleSection: article.category,
        inLanguage: 'fr-FR',
        isPartOf: {
          '@type': 'Blog',
          name: 'Blog Body by CC',
          url: 'https://bodybycc.fr/blog',
        },
      });
      document.head.appendChild(script);

      return () => {
        const el = document.getElementById('article-jsonld');
        if (el) el.remove();
      };
    }
  }, [article, slug, author]);

  useEffect(() => {
    if (headings.length === 0) return;
    const observer = new IntersectionObserver((entries) => { for (const entry of entries) { if (entry.isIntersecting) setActiveId(entry.target.id); } }, { rootMargin: '-100px 0px -70% 0px' });
    const timer = setTimeout(() => { headings.forEach(({ id }) => { const el = document.getElementById(id); if (el) observer.observe(el); }); }, 100);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [headings, processedContent]);

  if (!article) {
    return (
      <section className="section-padding bg-background text-center">
        <h1 className="font-heading text-2xl font-bold text-foreground">Article introuvable</h1>
        <Link to="/blog" className="mt-4 inline-flex items-center gap-2 text-primary font-heading text-sm"><ChevronRight size={16} className="rotate-180" /> Retour au blog</Link>
      </section>
    );
  }

  const scrollToHeading = (id: string) => {
    const el = document.getElementById(id);
    if (el) { window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 100, behavior: 'smooth' }); setTocOpen(false); }
  };

  return (
    <>
      {/* Breadcrumb */}
      <section className="px-6 lg:px-8 py-4 bg-background border-b border-border">
        <div className="max-w-4xl mx-auto flex items-center gap-2 text-xs text-muted-foreground">
          <Link to="/" className="hover:text-primary transition-colors">Accueil</Link>
          <ChevronRight size={12} />
          <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
          <ChevronRight size={12} />
          <span className="text-foreground truncate">{article.title}</span>
        </div>
      </section>

      {/* Hero */}
      <section className="px-6 lg:px-8 pt-10 pb-8 bg-background">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mb-5">
            <span className="font-heading font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">{article.category}</span>
          </div>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground leading-tight mb-6">{article.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><Calendar size={13} /> {new Date(article.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            <span className="flex items-center gap-1.5"><Clock size={13} /> {article.readTime}</span>
            <span className="flex items-center gap-1.5"><User size={13} /> {author}</span>
            <button onClick={() => window.print()} className="flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors ml-auto" title="Imprimer"><Printer size={13} /> Imprimer</button>
          </div>
        </div>
      </section>

      {/* Summary */}
      <section className="px-6 lg:px-8 pb-4 bg-background">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-xl border-2 border-primary/20 bg-primary/5 p-5 md:p-6">
            <h3 className="font-heading text-sm font-bold text-foreground mb-4 flex items-center gap-2"><CheckCircle2 size={16} className="text-primary" />{article.summaryTitle}</h3>
            <ul className="space-y-2">
              {article.summaryPoints.map((point, i) => (
                <li key={i} className="flex items-start gap-2.5 text-xs text-muted-foreground"><CheckCircle2 size={14} className="text-primary shrink-0 mt-0.5" /><span>{point}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Mobile TOC */}
      {headings.length > 0 && (
        <div className="lg:hidden px-6 pb-4 bg-background">
          <div className="max-w-4xl mx-auto">
            <button onClick={() => setTocOpen(!tocOpen)} className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-lg border border-border bg-card text-sm font-heading font-medium text-foreground">
              <span className="flex items-center gap-2"><List size={16} className="text-primary" /> Sommaire</span>
              {tocOpen ? <X size={16} /> : <ChevronRight size={16} />}
            </button>
            {tocOpen && (
              <nav className="mt-2 p-4 rounded-lg border border-border bg-card">
                <ol className="space-y-2">
                  {headings.map((h, i) => (
                    <li key={h.id}><button onClick={() => scrollToHeading(h.id)} className={`text-left text-sm text-muted-foreground hover:text-primary transition-colors flex items-start gap-2 ${h.level === 3 ? 'pl-4' : ''}`}><span className="text-primary/40 font-heading text-xs font-bold mt-0.5 shrink-0">{tocNumbers[i]}</span><span className="line-clamp-2">{h.text}</span></button></li>
                  ))}
                </ol>
              </nav>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <section className="px-6 lg:px-8 py-8 md:py-12 bg-background">
        <div className="max-w-6xl mx-auto flex gap-10">
          {headings.length > 0 && (
            <aside className="hidden lg:block w-64 shrink-0">
              <div className="sticky top-28">
                <h4 className="font-heading text-xs font-bold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2"><List size={14} className="text-primary" /> Sommaire</h4>
                <nav>
                  <ol className="space-y-1 border-l-2 border-border">
                    {headings.map((h, i) => (
                      <li key={h.id}><button onClick={() => scrollToHeading(h.id)} className={`block text-left w-full py-1.5 text-xs leading-snug transition-all border-l-2 -ml-[2px] ${h.level === 3 ? 'pl-8' : 'pl-4'} ${activeId === h.id ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}><span className="flex items-start gap-2"><span className={`font-heading font-bold shrink-0 mt-px ${activeId === h.id ? 'text-primary' : 'text-primary/30'}`}>{tocNumbers[i]}</span><span className="line-clamp-2">{h.text}</span></span></button></li>
                    ))}
                  </ol>
                </nav>
              </div>
            </aside>
          )}
          <div className="flex-1 min-w-0 max-w-4xl">
            <div className="article-content prose prose-sm md:prose-base max-w-none" dangerouslySetInnerHTML={{ __html: processedContent }} />
            {tags.length > 0 && (<div className="mt-12 pt-8 border-t border-border"><p className="font-heading font-semibold text-foreground text-sm mb-3">Mots-clés</p><ArticleTags tags={tags} /></div>)}
            <div className="mt-8 pt-8 border-t border-border"><ShareButtons title={article.title} url={articleUrl} /></div>
            <div className="mt-8"><AuthorCard /></div>
          </div>
        </div>
      </section>

      <CTASection title={article.ctaLabel} subtitle="Réservez votre soin chez Body by CC à Sevran et profitez de l'expertise d'une infirmière diplômée." buttonLabel={article.ctaLabel} buttonLink={article.ctaLink} isExternal={article.ctaLink.startsWith('http')} />

      {related.length > 0 && (
        <section className="px-6 lg:px-8 py-12 md:py-16 bg-card">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl font-bold text-foreground mb-8">À lire également</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">{related.slice(0, 3).map((r, i) => (<BlogCard key={r.slug} article={r} index={i} />))}</div>
          </div>
        </section>
      )}
    </>
  );
};

export default BlogArticle;
